import { IIndirectTaxPortal } from '@commenda/api/indirect-tax/registrations';
import { Alert, Input, LoadingIndicator } from '@commenda/kakashi';
import { toTitleCasePreservingAcronyms } from '@commenda/utils';
import { trimIndirectTaxCredentialFieldValue } from '../utils/trimIndirectTaxCredentialFieldValue';
import { IndirectTaxCredentialFileUpload } from './IndirectTaxCredentialFileUpload';

interface IndirectTaxRegistrationCredentialsStepProps {
  selectedPortal: IIndirectTaxPortal | null;
  corporationId?: number;
  companyId?: number;

  isCheckingCredentials: boolean;

  credentialsFound: boolean;

  registrationFields: Record<string, string>;

  credentialFields: Record<string, string>;

  onRegistrationFieldChange: (key: string, value: string) => void;
  onCredentialFieldChange: (key: string, value: string) => void;

  forceShowFields?: boolean;
}

export function IndirectTaxRegistrationCredentialsStep({
  selectedPortal,
  corporationId,
  companyId,
  isCheckingCredentials,
  credentialsFound,
  registrationFields,
  credentialFields,
  onRegistrationFieldChange,
  onCredentialFieldChange,
  forceShowFields = false,
}: IndirectTaxRegistrationCredentialsStepProps) {
  if (isCheckingCredentials) {
    return (
      <div className="flex items-center justify-center py-4xl">
        <LoadingIndicator />
      </div>
    );
  }

  const allFields = selectedPortal?.credentialFields || [];
  const sortedFields = [...allFields].sort((a, b) => a.displayOrder - b.displayOrder);

  const registrationLevelFields = sortedFields.filter((f) => f.isUniquePerRegistration);
  const credentialLevelFields = sortedFields.filter((f) => !f.isUniquePerRegistration);

  return (
    <div className="flex flex-col gap-3xl">
      {registrationLevelFields.map((field) =>
        field.credentialType === 'File' && corporationId ? (
          <IndirectTaxCredentialFileUpload
            key={field.credentialKey}
            fieldKey={field.credentialKey}
            fieldName={field.credentialName}
            required={field.required}
            value={registrationFields[field.credentialKey] || ''}
            onChange={onRegistrationFieldChange}
            corporationId={corporationId}
            companyId={companyId}
          />
        ) : (
          <div key={field.credentialKey} className="flex flex-col gap-xs">
            <div className="flex flex-col gap-xxs">
              <p className="txt-small-bold text-text-secondary">
                {toTitleCasePreservingAcronyms(field.credentialName)}
                {field.required ? (
                  <span className="text-utility-violet-600 ml-0.5">*</span>
                ) : (
                  <span className="txt-small-regular text-text-tertiary ml-1">
                    (optional)
                  </span>
                )}
              </p>
            </div>
            <Input
              data-testid={`credential-${field.credentialKey}`}
              type={field.credentialType === 'SecureString' ? 'password' : 'text'}
              value={registrationFields[field.credentialKey] || ''}
              onChange={(e) =>
                onRegistrationFieldChange(field.credentialKey, e.target.value)
              }
              onBlur={() => {
                const raw = registrationFields[field.credentialKey] || '';
                const trimmed = trimIndirectTaxCredentialFieldValue(
                  raw,
                  field.credentialType
                );
                if (trimmed !== raw) {
                  onRegistrationFieldChange(field.credentialKey, trimmed);
                }
              }}
              placeholder={field.helperText}
              className="h-11"
            />
          </div>
        )
      )}

      {credentialLevelFields.length === 0 ? (
        <Alert
          variant="info"
          title="No portal credentials required"
          description="The selected portal does not have any credential fields. You can save and mark this registration as valid."
        />
      ) : credentialsFound && !forceShowFields ? (
        <Alert
          variant="info"
          title="Credentials already on file"
          description="We already have credentials stored for this portal. Your existing credentials will be used for this registration."
        />
      ) : (
        <>
          {credentialLevelFields.map((field) =>
            field.credentialType === 'File' && corporationId ? (
              <IndirectTaxCredentialFileUpload
                key={field.credentialKey}
                fieldKey={field.credentialKey}
                fieldName={field.credentialName}
                helperText={field.helperText}
                required={field.required}
                value={credentialFields[field.credentialKey] || ''}
                onChange={onCredentialFieldChange}
                corporationId={corporationId}
                companyId={companyId}
              />
            ) : (
              <div key={field.credentialKey} className="flex flex-col gap-xs">
                <div className="flex flex-col gap-xxs">
                  <p className="txt-small-bold text-text-secondary">
                    {toTitleCasePreservingAcronyms(field.credentialName)}
                    {field.required ? (
                      <span className="text-utility-violet-600 ml-0.5">*</span>
                    ) : (
                      <span className="txt-small-regular text-text-tertiary ml-1">(optional)</span>
                    )}
                  </p>
                </div>
                <Input
                  data-testid={`credential-${field.credentialKey}`}
                  type={field.credentialType === 'SecureString' ? 'password' : 'text'}
                  value={credentialFields[field.credentialKey] || ''}
                  onChange={(e) =>
                    onCredentialFieldChange(field.credentialKey, e.target.value)
                  }
                  onBlur={() => {
                    const raw = credentialFields[field.credentialKey] || '';
                    const trimmed = trimIndirectTaxCredentialFieldValue(raw, field.credentialType);
                    if (trimmed !== raw) onCredentialFieldChange(field.credentialKey, trimmed);
                  }}
                  placeholder={field.helperText}
                  className="h-11"
                />
              </div>
            )
          )}
        </>
      )}
    </div>
  );
}
